import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainCard from "../../components/MainCard/MainCard";
import Table from "../../components/Table/Table";
import { IClientCSVData, IMyClientsParams, IRowMyClientsReports } from "../../constants/interfaces";
import SearchIcon from "@mui/icons-material/Search";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { ClientsStore } from "../../store/MyClientsStore";
import { ContextModel, SearchModel } from "../../models/Common";
import { getSnapshot, Instance } from "mobx-state-tree";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { Column, columnTypes, TableOptions } from "../../components/DataTable/datatable.models";
import { Box } from "@mui/material";
import IClientReports from "../../assets/svg/IClientReports";

const CancelledClients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevParams = location.state && location.state.keepParams ? getSnapshot(ClientsStore.params) : undefined;
  const pageWasRendered = useRef(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [param, setParam] = useState<IMyClientsParams>({
    fullName: prevParams ? prevParams.fullName : undefined,
  });
  const [rows, setRows] = useState<IRowMyClientsReports[] | []>([]);
  const [context, setContext] = useState<Instance<typeof ContextModel> | null>();
  const [searchItems, setSearchItems] = useState<Instance<typeof SearchModel>[]>([]);
  const [defaultSearch, setDefaultSearch] = useState<Instance<typeof SearchModel>>();

  /**
   * columns object to be passed to the Table component with the settings for each one.
   */
  const columns: Column[] = [
    {
      name: "Full Name",
      value: "fullName",
      sortable: false,
      mobile: true,
    },
    {
      name: "VIP Cancel Date",
      value: "vipCancellationDate",
      type: columnTypes.DATE,
      sortable: false,
      mobile: true,
    },
    {
      name: "Email",
      value: "email",
      sortable: false,
      mobile: false,
    },
    {
      name: "Phone",
      value: "phone",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "",
      type: columnTypes.CUSTOM,
      renderCell: () => <RemoveRedEyeOutlined sx={{ fontSize: "2rem" }} className="icon-column" />,
      mobile: true,
    },
  ];

  const getOption = (option: any) => {
    setParam({ ...param, fullName: option?.value ?? undefined });
  };

  const getDataForCSV = async (): Promise<IClientCSVData[]> => {
    const paramToCSV = { ...param, page: undefined, size: undefined, withoutPagination: true };
    const data = await ClientsStore.fetchClientReports(paramToCSV, "cancelled", true);
    return data ?? [];
  };

  /**
   * It will be called on every row click
   */
  const onRowClicked = (row: object) => {
    navigate(`${ROUTE_PATHS.MY_CLIENTS_TABS.replace(":id", (row as IRowMyClientsReports)?.id.toString())}`, {
      state: { backLinkText: "Client Report", backLinkUrl: ROUTE_PATHS.CANCELLED_CLIENTS },
    });
  };

  /**
   * It will be called every time the sorting or the pagination changed.
   */
  const onTableOptionsChanged = (tableParams: TableOptions) => {
    //save table pagination and sorting
    setParam({
      ...param,
      page: tableParams.page,
      size: tableParams.pageSize,
    });
  };

  const getCancelledClients = async () => {
    try {
      const result = await ClientsStore.fetchCancelledReport(param);
      setRows(result.items);
      setContext(result.context);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const getSearchClientName = async () => {
    ClientsStore.fetchSearch({ status: "cancelled" } as IMyClientsParams);
    setSearchItems(ClientsStore.search);
    if (prevParams?.fullName) {
      const selectedUser = ClientsStore.search.find((item) => item.value === prevParams.fullName);
      setDefaultSearch(selectedUser);
    }
  };

  useEffect(() => {
    if (pageWasRendered.current) {
      const fetchData = async () => {
        setLoadingTable(true);
        try {
          await getCancelledClients();
        } catch (error) {
          console.log(error);
        } finally {
          setLoadingTable(false);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  /**
   * On page load, get the data to fill the filters.
   */
  useEffect(() => {
    const fetchData = async () => {
      setLoadingTable(true);
      await Promise.all([getSearchClientName()]);
      setLoadingTable(false);
    };
    fetchData();
    pageWasRendered.current = true;
  }, []);

  return (
    <MainCard>
      <Table
        getCSVData={getDataForCSV}
        idTable="cancelled-table"
        header={
          <Box
            sx={{
              padding: 1.25,
              fontSize: "1.4rem",
              textAlign: "left",
              marginBottom: 0.5,
              borderBottom: 2,
              borderColor: "divider",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>Cancelled Clients:</span>
            {" Reach out to cancelled clients to come back."}
          </Box>
        }
        tableProps={{
          columns: columns,
          data: rows,
          totalRows: context?.totalRows ?? 0,
          loading: loadingTable,
          handleTableOptionsChanged: onTableOptionsChanged,
          handleRowClicked: onRowClicked,
          expandRowOnMobileBreakpoint: true,
          mobileBreakpoint: 900,
          paginationValue:
            prevParams && prevParams.page && prevParams.size
              ? {
                  page: prevParams.page - 1,
                  size: prevParams.size,
                }
              : undefined,
        }}
        autocompleteFilterProps={{
          //singleselection
          withInputAutocompleteSingle: true,
          singleValue: defaultSearch,
          iconSingle: SearchIcon,
          optionsSingle: searchItems,
          labelSingle: "Search",
          placeholderSingle: "Full Name",
          sxSingle: { width: "22rem" },
          onChange: getOption,
          idSingle: "clients-input-search",
        }}
        downloadCSVButtonProps={{
          id: "client-btn-dowload",
          show: true,
          text: "Download",
          disabled: rows?.length === 0 ? true : false,
          startIcon: <SaveAltIcon />,
          variant: "outlined",
          size: "large",
        }}
        buttonsProps={[
          {
            id: "client-btn-reports",
            show: true,
            text: "Client Reports",
            startIcon: <IClientReports width="2rem" height="2rem" />,
            variant: "outlined",
            size: "large",
            onClick: () => {
              navigate(`${ROUTE_PATHS.CLIENT_REPORTS}`);
            },
          },
        ]}
      />
    </MainCard>
  );
};

export default CancelledClients;
